// Primary data processing for retirement calculator (Chart.js format)
//
// TODO: FUTURE IMPROVEMENTS - Refactor into Generic Chart Data Utilities
// This processor has high reusability potential for other chart components:
//
// Current Limitations:
// - Hardcoded retirement-specific field names (startingValueSF, closingValueFull, pensionSF)
// - Retirement-specific color mapping and logic
// - Complex asset processing tied to retirement data structure
// - Year-to-age conversion specific to user age
//
// Transformation Strategy:
// 1. Extract common patterns into generic functions in src/lib/chartDataUtils.ts
// 2. Make field mappings configurable via parameters
// 3. Separate color/styling logic from data processing
// 4. Create adapter functions for different input formats
//
// This would enable reuse across:
// - Salary Sacrifice projection charts (RetirementProjectionCharts)
// - Asset/Income projection composables (useIncomeProjections, useAssetProjections)
// - Any future Chart.js implementations
//
// Current data format compatibility:
// - Retirement Calculator: Complex nested {years: {itemData}} objects
// - Salary Sacrifice: Simple {label, data: number[]} arrays
// - Income Projections: {year, age, projection} format
//
import { format, subYears } from 'date-fns'
import type { RetirementData } from './types'

export interface ChartDataset {
  label: string
  data: number[]
  backgroundColor?: string
  borderColor?: string
  borderWidth?: number
  fill?: boolean
  tension?: number
  pointRadius?: number
  pointHoverRadius?: number
  hidden?: boolean
  metadata?: {
    tooltips: string[]
    years: string[]
    originalData: any[]
  }
}

export interface ChartData {
  labels: number[]
  datasets: ChartDataset[]
}

export const useRetirementCalculatorProcessor = () => {

  const mapSeriesData = (combinedAssetsData: any): ChartDataset => {
    const data: number[] = []
    const tooltips: string[] = []
    const years: string[] = []
    const originalData: any[] = []

    // Defensive check - ensure combinedAssetsData is a valid object
    if (!combinedAssetsData || typeof combinedAssetsData !== 'object' || Array.isArray(combinedAssetsData)) {
      console.warn('Invalid combinedAssetsData passed to mapSeriesData:', combinedAssetsData)
      return {
        label: 'Total assets',
        data: [],
        borderColor: '#0064FF',
        backgroundColor: 'rgba(0, 100, 255, 0.1)',
        borderWidth: 3,
        fill: false,
        tension: 0.1,
        pointRadius: 0,
        pointHoverRadius: 6,
        metadata: {
          tooltips: [],
          years: [],
          originalData: []
        }
      }
    }

    Object.keys(combinedAssetsData).forEach((mappingYearItem) => {
      // Add offset and cash savings in the assets
      combinedAssetsData[mappingYearItem].startingValue += 0
      // Use the closing value as the value for the graph
      let value = combinedAssetsData[mappingYearItem].startingValue
      // Add total savings
      if (combinedAssetsData[mappingYearItem].debtBalance) {
        value -= combinedAssetsData[mappingYearItem].debtBalance
      }

      data.push(Math.floor(value))
      years.push(mappingYearItem)
      originalData.push(combinedAssetsData[mappingYearItem])
      // Note: Tooltips will be generated by Chart.js callbacks, not pre-built HTML
      tooltips.push(`Net assets: $${Math.floor(value).toLocaleString()}`)
    })

    return {
      label: 'Total assets',
      data,
      borderColor: '#0064FF',
      backgroundColor: 'rgba(0, 100, 255, 0.1)',
      borderWidth: 3,
      fill: false,
      tension: 0.1,
      pointRadius: 0,
      pointHoverRadius: 6,
      metadata: {
        tooltips,
        years,
        originalData
      }
    }
  }

  const formatInitialSeriesData = (graphData: any, userData: RetirementData) => {
    const combinedAssetsData: any = {}
    const datasets: ChartDataset[] = []
    const labels: string[] = []
    const userBirthYear = new Date().getFullYear() - (userData.age || 0)

    console.log('Processing asset data:', graphData.asset)
    console.log('Number of assets in API response:', graphData.asset?.length || 0)

    // Process each asset type
    graphData.asset.forEach((el: any, index: number) => {
      console.log(`Processing asset ${index}:`, el)
      let assetName = ''
      const data: number[] = []
      const tooltips: string[] = []
      const years: string[] = []
      const originalData: any[] = []
      let type = ''

      if (el.years) {
        const yearKeys = Object.keys(el.years)
          .filter((year) => Number(year) - userBirthYear <= 92)
          .sort((a, b) => Number(a) - Number(b))

        yearKeys.forEach((mappingYearItem) => {
          assetName = el.years[mappingYearItem].name
          const isSuper = assetName.toLowerCase() === 'super'
          const isPartnerSuper = assetName.toLowerCase() === 'partner super'
          type = el.years[mappingYearItem].type

          if (isSuper || isPartnerSuper) {
            el.years[mappingYearItem].startingValue = el.years[mappingYearItem].startingValueSF
            el.years[mappingYearItem].closingValue = el.years[mappingYearItem].closingValueFull
          }

          const startingValue = el.years[mappingYearItem].startingValue
          const closingValue = el.years[mappingYearItem].closingValue
          const annualGrowth = closingValue - startingValue

          // Build combined assets data for net assets calculation
          if (combinedAssetsData[mappingYearItem]) {
            combinedAssetsData[mappingYearItem] = {
              startingValue: combinedAssetsData[mappingYearItem].startingValue + startingValue,
              annualGrowth: combinedAssetsData[mappingYearItem].annualGrowth + annualGrowth,
              closingValue: combinedAssetsData[mappingYearItem].closingValue + closingValue,
              debtBalance: 0,
              year: mappingYearItem,
            }
          } else {
            combinedAssetsData[mappingYearItem] = {
              startingValue: startingValue,
              annualGrowth: annualGrowth,
              closingValue: closingValue,
              debtBalance: 0,
              year: mappingYearItem,
            }
          }

          data.push(Math.floor(startingValue))
          years.push(mappingYearItem)
          originalData.push(el.years[mappingYearItem])
          tooltips.push(`${assetName}: $${Math.floor(startingValue).toLocaleString()}`)
        })

        // Create labels from the first asset (they should all have same years)
        if (labels.length === 0) {
          labels.push(...yearKeys)
        }
      }

      // Add dataset for this asset type if it has data
      if (data.length > 0) {
        const hasNonZeroValues = data.some(value => value > 0)
        console.log(`Asset ${assetName}: ${data.length} data points, hasNonZeroValues: ${hasNonZeroValues}, data: [${data.slice(0, 3).join(', ')}...]`)

        const color = getAssetColor(assetName, type)
        datasets.push({
          label: assetName === 'Net assets' ? 'Total assets' : assetName,
          data,
          borderColor: color,
          backgroundColor: `${color}20`, // 20% opacity
          borderWidth: assetName.toLowerCase() === 'net assets' ? 4 : 2,
          fill: false,
          tension: 0.1,
          pointRadius: 0,
          pointHoverRadius: 6,
          hidden: false, // Show all asset types by default
          metadata: {
            tooltips,
            years,
            originalData
          }
        })
      } else {
        console.log(`Asset ${assetName}: No data points, skipping dataset creation`)
      }
    })

    return { combinedAssetsData, datasets, labels }
  }

  const mapAllRetCalcAssetsData = (graphData: any, userData: RetirementData): ChartData => {
    console.log('=== mapAllRetCalcAssetsData called ===')
    const { combinedAssetsData, datasets, labels } = formatInitialSeriesData(graphData, userData)

    // Add the net assets dataset at the beginning
    const netAssetsDataset = mapSeriesData(combinedAssetsData)

    console.log(`Final asset datasets: ${datasets.length} individual + 1 total = ${datasets.length + 1} total datasets`)
    datasets.forEach((dataset, index) => {
      console.log(`  Dataset ${index}: ${dataset.label} (${dataset.data.length} points)`)
    })

    return {
      labels: labels.map(year => {
        const userAge = Number(year) - (new Date().getFullYear() - (userData.age || 0))
        return userAge.toString()
      }),
      datasets: [netAssetsDataset, ...datasets]
    }
  }

  const getAssetColor = (assetName: string, type: string): string => {
    const name = assetName.toLowerCase()
    const assetType = type?.toLowerCase()

    console.log(`Determining color for asset: ${name}, type: ${assetType}`)

    // Match CFS colors - by name first
    if (name === 'net assets' || name === 'total assets') return '#0064FF'
    if (name === 'super') return '#00885E'  // Match CFS Super color
    if (name === 'partner super') return '#01CC9B'  // Match CFS Partner Super color
    if (name === 'home') return '#FF69B4'
    if (name === 'shares') return '#fd8900'  // Match CFS Shares color
    if (name === 'property') return '#000032'  // Match CFS Property color
    if (name === 'managed funds' || name === 'managedfunds') return '#d70181'  // Match CFS Managed Funds color
    if (name === 'alternative') return '#696969'  // Match CFS Alternative color
    if (name === 'cash' || name === 'cashandfixed') return '#32CD32'

    // By type if name doesn't match
    if (assetType === 'home') return '#FF69B4'
    if (assetType === 'debt') return '#9370DB'
    if (assetType === 'shares') return '#fd8900'  // Match CFS Shares color
    if (assetType === 'alternative') return '#696969'  // Match CFS Alternative color
    if (assetType === 'cashandfixed') return '#32CD32'
    if (assetType === 'property') return '#000032'  // Match CFS Property color
    if (assetType === 'managedfunds') return '#d70181'  // Match CFS Managed Funds color

    return '#A0A0A0' // Match default color
  }

  // Income chart specific functions
  const mapIncomeData = (incomeChartData: any, userData: RetirementData, partnerData: RetirementData, enablePartnerForm: boolean): ChartData => {
    console.log('=== mapIncomeData called ===')
    const labels: number[] = []
    const datasets: ChartDataset[] = []

    // Income categories to process - using exact labels that IncomeProjectionChart expects
    const incomeCategories = [
      { key: 'salary', label: 'Salary' },
      { key: 'superSF', label: 'Super' },
      { key: 'pensionSF', label: 'Pension' },
      { key: 'partnerSalary', label: "Partner Salary", partnerOnly: true },
      { key: 'partnerSuperSF', label: "Partner Super", partnerOnly: true },
      { key: 'partnerPensionSF', label: "Partner Pension", partnerOnly: true },
      { key: 'shares', label: 'Shares' },
      { key: 'property', label: 'Property' },
      { key: 'cashAndFixed', label: 'Cash', mappedLabel: 'Cash' }, // Note: might need to check if this maps to anything specific
      { key: 'managedFunds', label: 'Managed Funds' },
      { key: 'alternative', label: 'Alternative' }
    ]

    // Build labels (ages)
    Object.keys(incomeChartData).forEach(year => {
      const userAge = Number(year) - (new Date().getFullYear() - (userData.age || 0))
      labels.push(userAge)
    })

    // Build datasets for each income category
    incomeCategories.forEach(category => {
      // Skip partner categories if partner form is disabled
      if (category.partnerOnly && !enablePartnerForm) return

      const data: number[] = []

      // For partner categories, create dataset if partner form is enabled and data exists with values > 0
      // For user categories, only create dataset if there's actual data > 0
      const hasData = category.partnerOnly
        ? enablePartnerForm && Object.keys(incomeChartData).some(year =>
            incomeChartData[year][category.key] && incomeChartData[year][category.key] > 0
          )
        : Object.keys(incomeChartData).some(year =>
            incomeChartData[year][category.key] && incomeChartData[year][category.key] > 0
          )

      if (hasData) {
        Object.keys(incomeChartData).forEach(year => {
          data.push(Math.round(incomeChartData[year][category.key] || 0))
        })

        const hasNonZeroIncome = data.some(value => value > 0)
        console.log(`Income ${category.label}: hasData: ${hasData}, hasNonZeroIncome: ${hasNonZeroIncome}, data: [${data.slice(0, 3).join(', ')}...]`)

        datasets.push({
          label: category.label,
          data,
          // Let IncomeProjectionChart handle colors via its mapColor function
          metadata: {
            tooltips: data.map(value => `${category.label}: $${value.toLocaleString()}`),
            years: Object.keys(incomeChartData),
            originalData: Object.keys(incomeChartData).map(year => incomeChartData[year])
          }
        })
      } else {
        console.log(`Income ${category.label}: No data found, skipping dataset creation`)
      }
    })

    console.log(`Final income datasets: ${datasets.length} total datasets`)
    datasets.forEach((dataset, index) => {
      console.log(`  Dataset ${index}: ${dataset.label} (${dataset.data.length} points)`)
    })

    return { labels, datasets }
  }

  // Legacy format converters for IncomeProjectionChart compatibility
  const convertToIncomeProjectionFormat = (chartData: ChartData): Array<{ label: string, data: number[] }> => {
    return chartData.datasets.map(dataset => ({
      label: dataset.label,
      data: dataset.data
    }))
  }

  const convertToAssetsProjectionFormat = (chartData: ChartData): Array<{ label: string, data: number[] }> => {
    return chartData.datasets.map(dataset => ({
      label: dataset.label,
      data: dataset.data
    }))
  }

  // Generate raw data structure from chart data for IncomeProjectionChart tooltips and annotations
  const generateRawDataFromIncome = (incomeData: any, userData: RetirementData): Record<string, Array<{ year: number, age: number, projection: number }>> => {
    const raw: Record<string, Array<{ year: number, age: number, projection: number }>> = {
      salary: [],
      super: [],
      pension: [],
      partnerSalary: [],
      partnerSuper: [],
      partnerPension: [],
      shares: [],
      property: [],
      cashAndFixed: [],
      managedFunds: [],
      alternative: []
    }

    if (!incomeData || typeof incomeData !== 'object') {
      return raw
    }

    Object.keys(incomeData).forEach(year => {
      const yearData = incomeData[year]
      const yearNum = Number(year)
      const userAge = yearNum - (new Date().getFullYear() - (userData.age || 0))

      // Always add salary entries (even if 0) to ensure retirement line calculations work
      raw.salary.push({ year: yearNum, age: userAge, projection: yearData.salary || 0 })
      if (yearData.superSF > 0) {
        raw.super.push({ year: yearNum, age: userAge, projection: yearData.superSF })
      }
      if (yearData.pensionSF > 0) {
        raw.pension.push({ year: yearNum, age: userAge, projection: yearData.pensionSF })
      }
      if (yearData.partnerSalary > 0) {
        raw.partnerSalary.push({ year: yearNum, age: userAge, projection: yearData.partnerSalary })
      }
      if (yearData.partnerSuperSF > 0) {
        raw.partnerSuper.push({ year: yearNum, age: userAge, projection: yearData.partnerSuperSF })
      }
      if (yearData.partnerPensionSF > 0) {
        raw.partnerPension.push({ year: yearNum, age: userAge, projection: yearData.partnerPensionSF })
      }
      if (yearData.shares > 0) {
        raw.shares.push({ year: yearNum, age: userAge, projection: yearData.shares })
      }
      if (yearData.property > 0) {
        raw.property.push({ year: yearNum, age: userAge, projection: yearData.property })
      }
      if (yearData.cashAndFixed > 0) {
        raw.cashAndFixed.push({ year: yearNum, age: userAge, projection: yearData.cashAndFixed })
      }
      if (yearData.managedFunds > 0) {
        raw.managedFunds.push({ year: yearNum, age: userAge, projection: yearData.managedFunds })
      }
      if (yearData.alternative > 0) {
        raw.alternative.push({ year: yearNum, age: userAge, projection: yearData.alternative })
      }
    })

    return raw
  }

  // Generate raw data structure for assets charts
  const generateRawDataFromAssets = (assetData: any, userData: RetirementData): Record<string, Array<{ year: number, age: number, projection: number }>> => {

    const raw: Record<string, Array<{ year: number, age: number, projection: number }>> = {}

    if (!assetData || !Array.isArray(assetData)) {
      return raw
    }

    const userBirthYear = new Date().getFullYear() - (userData.age || 0)

    // Process each asset type from the API response
    assetData.forEach((assetGroup: any, index: number) => {
      if (!assetGroup.years) {
        return
      }

      const map = {
        home: 'home',
        super: 'userSuper',
        'partner super': 'partnerSuper',
        property: 'property',
        shares: 'shares',
        cashandfixed: 'cashAndFixed',
        managedfunds: 'managedFunds',
        alternative: 'alternative'
      } as Record<string, string>
      const thisYear = new Date().getFullYear()
      const asset = map[assetGroup.years[thisYear].name.toLowerCase()]
      raw[asset] = []


      const yearKeys = Object.keys(assetGroup.years)
        .filter((year) => Number(year) - userBirthYear <= 92)
        .sort((a, b) => Number(a) - Number(b))

      yearKeys.forEach((year) => {
        const yearData = assetGroup.years[year]
        const yearNum = Number(year)
        const userAge = yearNum - userBirthYear

        const assetName = yearData.name?.toLowerCase() || ''
        const projection = yearData.startingValue || 0



        raw[asset].push({ year: yearNum, age: userAge, projection })
      })
    })

    return raw
  }

  return {
    mapSeriesData,
    formatInitialSeriesData,
    mapAllRetCalcAssetsData,
    mapIncomeData,
    getAssetColor,
    convertToIncomeProjectionFormat,
    convertToAssetsProjectionFormat,
    generateRawDataFromIncome,
    generateRawDataFromAssets
  }
}
